import { __decorate, __metadata } from '../../node_modules/tslib/tslib.es6.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import { html as x } from '../../node_modules/lit-html/lit-html.js';
import '../../node_modules/lit-element/lit-element.js';
import { property as n } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/property.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/query-assigned-elements.js';
import { ifDefined as l } from '../../node_modules/lit-html/directives/if-defined.js';
import getClassName from '../../node_modules/@fmr-ap142296/brick-helpers/dist/src/functions/get-class-name.js';
import { ScopedElementsMixin } from '../utilities/scoped-elements/scoped-elements.mixin.js';
import { PvdSlotifyBaseComponent } from '../pvd-base-component/pvd-slotify-base-component.js';
import { StandardSize, Size } from '../pvd-utils/enums/size.js';

const DEFAULT_CHECKED = false;
const DEFAULT_DISABLED = false;
const DEFAULT_ERROR = false;
const DEFAULT_INDETERMINATE = false;
const DEFAULT_LABEL_HIDDEN = false;
const DEFAULT_SELECT_ALL = false;
const DEFAULT_SIZE = StandardSize.medium;
const DEFAULT_REQUIRED = false;
const CHECKBOX_INPUT_PROTECTED_ATTR_MAP = new Map([
    ['aria-describedby', 'pvd-aria-describedby'],
    ['aria-disabled', 'pvd-disabled'],
    ['aria-invalid', 'pvd-error'],
    ['aria-required', 'pvd-required'],
    ['checked', 'pvd-checked'],
    ['disabled', 'pvd-disabled'],
    ['id', 'pvd-id'],
    ['name', 'pvd-name'],
    ['required', 'pvd-required'],
    ['value', 'pvd-value'],
]);
/**
 * Providence Checkbox component.
 * @see {@link https://providence.fmr.com/4.x/components/checkbox/checkbox.html|Checkbox documentation}
 */
class PvdCheckbox extends ScopedElementsMixin(PvdSlotifyBaseComponent) {
    constructor() {
        super(...arguments);
        this.pvdChecked = DEFAULT_CHECKED;
        this.pvdDisabled = DEFAULT_DISABLED;
        this.pvdError = DEFAULT_ERROR;
        this.pvdIndeterminate = DEFAULT_INDETERMINATE;
        this.pvdLabelHidden = DEFAULT_LABEL_HIDDEN;
        this.pvdRequired = DEFAULT_REQUIRED;
        this.pvdSelectAll = DEFAULT_SELECT_ALL;
        this.pvdSize = DEFAULT_SIZE;
        // #endregion
    }
    static get scopedElements() {
        // $namespace-$name-$extendingComponentName
        return {
            'pvd-checkbox-icon': customElements.get(`${this.namespace}-icon`),
        };
    }
    // #endregion
    // #region public methods
    //#endregion
    // #region private methods
    get generatedInputId() {
        if (!this._generatedInputId) {
            // Id must start with a letter
            this._generatedInputId = `pvd-checkbox-id-${PvdSlotifyBaseComponent.generateRandomNumber()}`;
        }
        return this._generatedInputId;
    }
    // #region events
    /**
     * Update the `pvdChecked` property
     */
    onChange() {
        this.pvdChecked = !this.pvdChecked;
        this.dispatchEvent(new CustomEvent('pvd-checkbox-change', {
            bubbles: true,
            composed: true,
            detail: {
                isSelectAll: this.pvdSelectAll,
                checked: this.pvdChecked,
            },
        }));
    }
    /**
     * Sets attributes on the input element that are coming from pvdNativeInputAttributes
     * @param prevAttr optional, if user updates pvdNativeInputAttributes, this will remove/update existing attributes
     */
    setNativeInputAttributes(prevAttr) {
        const targetElement = this.querySelector('input');
        this.setNativeAttributes(this.pvdNativeInputAttributes, CHECKBOX_INPUT_PROTECTED_ATTR_MAP, targetElement, prevAttr);
    }
    // #endregion
    // #region LitElement lifecycles
    connectedCallback() {
        super.connectedCallback();
        this._pvdCheckedInitial = this.pvdChecked;
    }
    updated(changedProperties) {
        super.updated(changedProperties);
        if (this.pvdNativeInputAttributes) {
            const prevAttr = changedProperties.get('pvdNativeInputAttributes');
            // if native attributes are being set, go through the logic of applying attributes to target element
            this.setNativeInputAttributes(prevAttr);
        }
    }
    renderIcon(iconName, modifierName) {
        return this.scopeTemplate `
      <pvd-checkbox-icon
        pvd-icon-class="pvd-checkbox__checkbox-wrap-icon pvd-checkbox__checkbox-wrap-icon--${modifierName}"
        pvd-name="${iconName}"
        pvd-size="${this.pvdSize === StandardSize.large ? Size.small : Size.extraSmall}"
      ></pvd-checkbox-icon>
    `;
    }
    renderText() {
        return x `<s-slot>${this.pvdText}</s-slot>`;
    }
    renderMicrocopy() {
        return this.isSlotUsed('microcopy')
            ? x `<div class="pvd-checkbox__microcopy"><s-slot name="microcopy"></s-slot></div>`
            : x `<s-slot name="microcopy"></s-slot>`;
    }
    render() {
        const classMap = {
            default: `${this.constructor.namespace}-checkbox-root`,
            modifierPrefix: 'pvd-checkbox',
            modifiers: [
                {
                    condition: this.pvdError,
                    className: 'error',
                },
                {
                    condition: this.pvdLabelHidden,
                    className: 'label-hidden',
                },
                {
                    condition: true,
                    className: `${this.pvdSize}`,
                },
            ],
        };
        return x `<div class="${getClassName(classMap)}"><input @blur="${this.bubbleEvent}" @change="${this.onChange}" @focus="${this.bubbleEvent}" ?checked="${this._pvdCheckedInitial}" ?disabled="${this.pvdDisabled}" ?required="${this.pvdRequired}" ?selectAll="${this.pvdSelectAll}" .checked="${this.pvdChecked}" .indeterminate="${this.pvdIndeterminate}" aria-describedby="${l(this.pvdAriaDescribedby)}" aria-disabled="${l(this.a11yBoolean(this.pvdDisabled))}" aria-invalid="${l(this.a11yBoolean(this.pvdError))}" aria-required="${l(this.a11yBoolean(this.pvdRequired))}" class="pvd-checkbox__checkbox" id="${this.pvdId || this.generatedInputId}" name="${l(this.pvdName)}" type="checkbox" value="${l(this.pvdValue)}"> <label class="pvd-checkbox__label" for="${this.pvdId || this.generatedInputId}"><div aria-hidden="true" class="pvd-checkbox__checkbox-wrap">${this.renderIcon('action__check', 'checked')} ${this.renderIcon('action__subtract', 'indeterminate')}</div><div class="pvd-checkbox__label-text">${this.renderText()}</div></label> ${this.renderMicrocopy()}</div>`;
    }
}
//#region Component configuration
PvdCheckbox.componentName = 'checkbox';
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-describedby' }),
    __metadata("design:type", String)
], PvdCheckbox.prototype, "pvdAriaDescribedby", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-checked',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-checked'),
    }),
    __metadata("design:type", Boolean)
], PvdCheckbox.prototype, "pvdChecked", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-disabled',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-disabled'),
    }),
    __metadata("design:type", Boolean)
], PvdCheckbox.prototype, "pvdDisabled", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-error',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-error'),
    }),
    __metadata("design:type", Boolean)
], PvdCheckbox.prototype, "pvdError", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-id' }),
    __metadata("design:type", String)
], PvdCheckbox.prototype, "pvdId", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-indeterminate',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-indeterminate'),
    }),
    __metadata("design:type", Boolean)
], PvdCheckbox.prototype, "pvdIndeterminate", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-label-hidden',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-label-hidden'),
    }),
    __metadata("design:type", Boolean)
], PvdCheckbox.prototype, "pvdLabelHidden", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-name' }),
    __metadata("design:type", String)
], PvdCheckbox.prototype, "pvdName", void 0);
__decorate([
    n({ type: Object, attribute: 'pvd-native-input-attributes' }),
    __metadata("design:type", Object)
], PvdCheckbox.prototype, "pvdNativeInputAttributes", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-required',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-required'),
    }),
    __metadata("design:type", Boolean)
], PvdCheckbox.prototype, "pvdRequired", void 0);
__decorate([
    n({ type: Boolean, attribute: 'pvd-select-all' }),
    __metadata("design:type", Boolean)
], PvdCheckbox.prototype, "pvdSelectAll", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-size',
        ...PvdSlotifyBaseComponent.hasChanged(StandardSize),
    }),
    __metadata("design:type", Number)
], PvdCheckbox.prototype, "pvdSize", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-text' }),
    __metadata("design:type", String)
], PvdCheckbox.prototype, "pvdText", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-value' }),
    __metadata("design:type", String)
], PvdCheckbox.prototype, "pvdValue", void 0);

export { CHECKBOX_INPUT_PROTECTED_ATTR_MAP, DEFAULT_CHECKED, DEFAULT_DISABLED, DEFAULT_ERROR, DEFAULT_INDETERMINATE, DEFAULT_LABEL_HIDDEN, DEFAULT_REQUIRED, DEFAULT_SELECT_ALL, DEFAULT_SIZE, PvdCheckbox };
//# sourceMappingURL=pvd-checkbox.js.map
